import { GridSortDirection } from '@mui/x-data-grid';

export type ProjectMember = {
  first_name: string;
  last_name: string;
  role_id: RoleEnum;
  id: string;
  type: string;
  email: string;
  project_role_id: number;
};

export enum ProjectTabsEnum {
  MY_PROJECTS_TAB = 'my',
  MY_OU_TAB = 'myOU',
  ALL_TAB = 'all',
  ACTIVE_TAB = 'active',
  INACTIVE_TAB = 'inactive',
}

export enum ProjectStatusEnum {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum ProjectTypeEnum {
  CONSUMER = 'Consumer',
  PRODUCER = 'Producer',
}

export type Project = {
  application_id: number;
  created: Date;
  created_by: string;
  creator?: Omit<ProjectMember, 'role_id'>;
  description: string;
  group_name: string;
  id: number;
  members: ProjectMember[];
  name: string;
  operating_unit_id: number;
  status: ProjectStatusEnum;
  type: ProjectTypeEnum;
  datasets: Dataset[];
  destination_datasets: Dataset[];
  updated: Date;
  wbs_code_id: number;
  cost_center_id?: number;
  wbs_code_is_active: boolean;
  cost_center_is_active: boolean;
};

export enum EmrStatusEnum {
  ACTIVE = 'ACTIVE',
  DELETED = 'DELETED',
  PENDING = 'PENDING',
}

export type Emr = {
  id: number;
  aws_studio_id: string;
  url: string;
  description: string;
  meta: unknown;
  status: EmrStatusEnum;
  created: Date;
  created_by: string;
  name: string;
  warnings?: string[];
};

export type Cluster = {
  aws_cluster_id: string;
  id: number;
  emr_id: number;
  name: string;
  status: ClusterStatusEnum;
  created: Date;
  creator: Omit<ProjectMember, 'role_id' | 'project_role_id'>;
  elapsed: number;
  instance_hours: number;
};

export type OperatingUnit = {
  created: Date;
  created_by: string;
  deleted: boolean;
  description: string;
  id: number;
  name: string;
  updated: Date;
  updated_by: string;
};

export enum ClusterStatusEnum {
  Running = 'RUNNING',
  Starting = 'STARTING',
  BootStrapping = 'BOOTSTRAPPING',
  Waiting = 'WAITING',
  Terminating = 'TERMINATING',
  Terminated = 'TERMINATED',
  TerminatedWithErrors = 'TERMINATED_WITH_ERRORS',
}

export type DatasetProjectRel = {
  created: Date;
  created_by: string;
  dataset_id: number;
  id: number;
  project_id: number;
  updated: Date;
  updated_by: string;
};

export enum DatasetAccessStatus {
  NO_ACCESS = 'No Access',
  PENDING = 'Access Requested',
  APPROVED = 'Access Approved',
}

export enum DatasetStatus {
  IN_PROGRESS = 'In Progress',
  READY_FOR_REVIEW = 'Ready for Review',
  LOCKED_SEARCHABLE = 'Locked-Searchable',
  LOCKED_RESTRICTED = 'Locked-Restricted',
}

export type DatasetAccessStatusParam = DatasetAccessStatus | null;

export enum DatasetSource {
  HDP = 'HDP',
  IMAGING = 'Imaging',
}

export enum DatasetTypeEnum {
  S3 = 's3',
  S3_EXTERNAL = 's3 external',
  DB = 'DB',
}

export type Dataset = {
  id: number;
  name: string;
  description: string;
  size: number;
  type: string;
  status: DatasetStatus;
  source: string;
  source_ou: string;
  data_classification: string;
  data_format: string[];
  anatomy: string[] | null;
  modality: string[] | null;
  access_status: DatasetAccessStatus;
  collection_source: string | null;
  de_identified: string;
  mdt_id: string;
  details_edit_allowed: boolean;
  status_edit_allowed: boolean;
  source_docs_upload_allowed: boolean;
  supportive_docs_upload_allowed: boolean;
  supportive_docs_edit_allowed: boolean;
  publish_to_atlan_allowed: boolean;
};

export enum DatasetSourceApiEnum {
  USER = 'user',
  M2M = 'm2m',
  SMUS = 'smus',
}

export type DatasetRes = {
  data: Dataset[];
  pagination: PaginationRes;
};

export type DatasetReq = {
  projectId?: number;
  isMy?: boolean;
  status?: DatasetAccessStatusParam;
  statusList?: string[];
  anatomyList?: string[];
  modalityList?: string[];
  ouOwnerList?: string[];
  dataSourceCategoryList?: string[];
  dataSourceType?: string;
  sort?: string;
  order?: GridSortDirection;
  search?: string;
} & PaginationReq;

export type DatasetDetails = Dataset & {
  location: {
    s3_bucket_name: string;
  };
  dimensionality: string[] | null;
  data_owner_name: string;
  legal_relationship_tag: string;
  legal_relationship_qualifier_tag: string | null;
  data_owner_network_id: string;
  active_directory_name: string | null;
  secondary_data_owners: string[] | null;
  secondary_data_owner_network_ids: string[] | null;
  data_uploaders: string[] | null;
  data_uploaders_network_ids: string[] | null;
  geo_location_descriptions: string[];
  geo_location_tags: string[];
  restriction_description: string[] | null;
  legal_relationship_qualifier: string;
  provenance: string;
  mdt_id: string | null;
  created: Date;
  created_by: string;
  updated: Date;
  updated_by: string;
  restriction_tag: string | null;
  source_api: DatasetSourceApiEnum;
  wbs_code_id: number;
  cost_center_id?: number;
  wbs_code_is_active: boolean;
  cost_center_is_active: boolean;
};

type DatasetCreationFields = Pick<
  DatasetDetails,
  | 'name'
  | 'description'
  | 'type'
  | 'source'
  | 'source_ou'
  | 'data_classification'
  | 'data_owner_name'
  | 'data_owner_network_id'
  | 'de_identified'
  | 'legal_relationship_qualifier_tag'
  | 'legal_relationship_tag'
  | 'provenance'
  | 'restriction_tag'
  | 'secondary_data_owner_network_ids'
  | 'secondary_data_owners'
  | 'data_uploaders'
  | 'data_uploaders_network_ids'
  | 'geo_location_tags'
  | 'collection_source'
  | 'status'
  | 'modality'
  | 'anatomy'
  | 'dimensionality'
  | 'mdt_id'
  | 'wbs_code_id'
  | 'cost_center_id'
>;

export type DatasetCreation = Partial<DatasetCreationFields> & {
  data_format?: string[];
  // mocked
  visit_type?: boolean;
  have_sites?: boolean;
};

export type ProjectRole = {
  created: Date;
  created_by: string;
  id: number;
  project_id: number;
  role_id: RoleEnum;
  updated: Date;
  updated_by: string;
  user_id: string;
};

export enum UserType {
  COGNITO = 'cognito',
  ACTIVE_DIRECTORY = 'active_directory',
  AZURE_AD = 'azure_ad',
}

export enum UserStatusEnum {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export type User = {
  role: {
    id: RoleEnum;
    name: string;
    description: string;
  };
  deleted: boolean;
  email: string;
  first_name: string;
  id: string;
  last_name: string;
  profile: {
    NTID: string;
  };
  status: UserStatusEnum;
  type: UserType;
  operating_unit_id: number;
  operating_unit_description: string;
  operating_unit_name: string;
  region?: RegionEnum;
  region_toggle_available?: boolean;
  is_admin: boolean;
};

export type PaginationReq = Omit<Partial<PaginationRes>, 'total'>;

export type ListRes<T> = {
  data: T;
  pagination: PaginationRes;
};

export enum RoleEnum {
  ADMINISTRATOR = 1,
  DATA_SCIENTIST = 2,
  TECHNICIAN_ANNOTATOR = 3,
  COMPLIANCE_ANALYST = 4,
  DATA_OWNER = 5,
  TESTER = 6,
  DEVELOPER = 7,
  SUPPORT = 8,
  GUEST = 9,
  DATA_UPLOADER = 10,
  TESTING_GOD = 11,
  MAINTAINER = 13,
  PENDING_WRITER = 16,
  PENDING_READER = 17,

  // project's roles
  OWNER = 12,
  WRITER = 14,
  READER = 15,
}

export type PaginationRes = {
  total: number;
  page: number;
  size: number;
};

export type WorkbenchConfiguration = Record<
  string,
  {
    description: string;
    id: string;
    instanceType: string;
    name: string;
    type: string;
    workbench_group: string;
  }[]
>;

export enum WorkbenchStatusEnum {
  PENDING = 'pending',
  INITIALIZING = 'initializing',
  RUNNING = 'running',
  SHUTTING_DOWN = 'shutting-down',
  STOPPED = 'stopped',
  STOPPING = 'stopping',
  TERMINATED = 'terminated',
}

export type Workbench = {
  access: string;
  autostop: boolean;
  created: Date;
  creator: Omit<ProjectMember, 'role_id'>;
  description: string;
  id: number;
  instance_id: string;
  instance_type: string;
  name: string;
  status: WorkbenchStatusEnum;
  type: string;
};

export type WorkbenchConnected = {
  aws_region: string;
  access_key_id: string;
  secret_access_key: string;
  session_token: string;
  port: number;
};

export type NewMember = {
  profile: {
    NTID: string;
  };
} & Pick<ProjectMember, 'id' | 'first_name' | 'email' | 'last_name'>;

export type MemberMutationResponse = {
  data: {
    user_id: string;
    emr_warning?: string;
  };
};
export type UploadDatasetFileReq = {
  datasetId: number;
  presignedUrl: string;
  supportiveDocs: boolean;
  file: File;
  onProgress: (percent: number) => void;
};

export type DatasetFile = {
  name: string;
  type: string;
  path: string;
  size: number;
  date_uploaded: number;
  date_obtained: number;
};

export enum DatasetCreationEnum {
  DATA_TYPE = 'data-type',
  COLLECTION_SOURCE = 'collection-source',
  DATA_CLASSIFICATION = 'data-classification',
  DEIDENTIFIED_STATUS = 'deidentified-status',
  RESTRICTION = 'restriction',
  LEGAL_RELATIONSHIP = 'legal-relationship',
  LEGAL_RELATIONSHIP_QUALIFIER = 'legal-relationship-qualifier',
  GEO_LOCATION = 'geo-location',
  STATUS = 'status',
  ANATOMY = 'anatomy',
  MODALITY = 'modality',
  DIMENSIONALITY = 'dimensionality',
}

export type FsRawNode = {
  name: string;
  path: string;
  is_dir: boolean;
  size: number | null;
  last_modified: string;
  children: FsRawNode[];
};

export type StorageStats = {
  size: number;
  file_count: number;
  folder_count: number;
};

export type DownloadTokenResponse = {
  token: string;
  expires_in: number;
};

export type UploadStorageFilesReq = {
  files: File[];
  onProgress: (percent: number) => void;
  folder_path: string;
};

export type UploadStorageFilesResponse = {
  data: { name: string; message?: string }[];
};

export type UploadLargeFileReq = {
  file: File;
  folder_path: string;
  presignedUrl: string;
  onProgress: (percent: number) => void;
};

export type GetPresignedUrlReq = {
  folder_path: string;
  file_name: string;
  content_type?: string;
};

export type PresignedUrlResponse = {
  data: {
    url: string;
  };
  method: string;
  expires_in: number;
  content_type: string | null;
};

export type CompleteUploadReq = {
  folder_path: string;
  file_name: string;
};

export type DatasetConnected = {
  aws_region: string;
  access_key_id: string;
  secret_access_key: string;
  session_token: string;
  bucket_name: string;
  bucket_folder: string;
};

export type DatasetConnectionRoles = {
  bucket_name: string;
  bucket_folder: string;
  role_arn: string | null;
};

export type RenameDatasetFileRequest = {
  datasetId: number;
  oldFilePath: string;
  newFilePath: string;
};

export type DownloadDatasetFileRequest = {
  datasetId: number;
  filePath: string;
};
export type DeleteDatasetFileRequest = { datasetId: number; filePath: string };

export type ETLScheduledJob = {
  id: string;
  name: string;
  file_name: string;
  start_date: string;
  frequency: ETLJobRecurrenceEnum;
};

export type ETLScheduledJobResponse = {
  data: ETLScheduledJob[];
  pagination: PaginationRes;
};

export enum ETLJobStatusEnum {
  SUBMITTED = 'SUBMITTED',
  PENDING = 'PENDING',
  SCHEDULED = 'SCHEDULED',
  QUEUED = 'QUEUED',
  RUNNING = 'RUNNING',
  SUCCESS = 'SUCCESS',
  CANCELLING = 'CANCELLING',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export type ETLJobHistory = {
  schedule_name: string;
  schedule_id: string;
  execution_date: Date;
  state: ETLJobStatusEnum;
  job_run_id: string;
  execution_time_seconds: number;
  state_details: string;
  attempt: number | null;
};

export type ETLJobHistoryResponse = {
  data: ETLJobHistory[];
  pagination: PaginationRes;
};

export enum ETLExecutionModeEnum {
  ON_DEMAND = 'on_demand',
  SCHEDULE = 'schedule',
}

export enum ETLJobRecurrenceEnum {
  NOT_REPEAT = 'NOT_REPEAT',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
}

export type CreateScheduledETLJobBody = {
  projectId: number;
  notebook_id: string;
  file_name: string;
  start_date?: Date;
  end_date?: Date;
  frequency?: ETLJobRecurrenceEnum;
  name: string;
};

export type EditScheduledETLJobBody = Partial<CreateScheduledETLJobBody> & {
  jobId: number;
  projectId: number;
};

export type ScheduleETLJob = {
  execution_mode: ETLExecutionModeEnum;
  time?: string;
} & CreateScheduledETLJobBody;

export type ETLJobsRequest = { projectId: number; search?: string } & PaginationReq;

export type UserDatasetsRequest = { userId: string; destinationType?: boolean };

export type DatasetAccessRequest = {
  datasetId: number;
  sort?: string;
  order?: GridSortDirection;
  search?: string;
} & PaginationReq;

export type DatasetAccess = {
  id: number;
  user_id: string;
  name: string;
  role_id: number;
  created: Date;
  created_by: string;
  deleted_at: Date | null;
  deleted_by: string | null;
};

export type DatasetAccessResponse = {
  data: DatasetAccess[];
  pagination: PaginationRes;
};

export type RevokeDatasetRequest = {
  datasetId: number;
  userId: string;
};

export type DatasetAffectedProject = {
  project_id: number;
  project_name: string;
  role_id: number;
};

export type ValidationRes = {
  is_unique: boolean;
  reason: string | null;
};

export enum RegionEnum {
  US = 'us',
  EU = 'eu',
}

export enum WbsCodeTypeEnum {
  COST_CENTER = 'cost_center',
  WBS_CODE = 'wbs_code',
}

export type WbsCodeCostCenterMergedType = {
  id: number;
  type: WbsCodeTypeEnum;
  verbose_name: string;
};

export type WbsCode = {
  id: number;
  wbs_element_displ_id: string;
  deleted: boolean;
  wbs_code_is_active: boolean;
};

export type CostCenter = {
  id: number;
  cost_center_id: string;
  deleted: boolean;
  cost_center_is_active: boolean;
};

export enum NoticeBannerStatusEnum {
  ACTIVE = 'active',
  SCHEDULED = 'scheduled',
  DELETED = 'deleted',
}

export type NoticeBanner = {
  id: number;
  message: string;
  start_datetime: string;
  end_datetime: string;
  deleted_at: string;
  deleted_by: string;
  status: NoticeBannerStatusEnum;
};

export type NoticeBannerPayload = {
  message: string;
  start_datetime: string | null;
  end_datetime: string | null;
};
